/**
 * TableEmptyState - a table with its columns defined and no documents yet.
 *
 * This state used to be a line of grey text and an outline button, sitting in
 * a `<td colSpan>` under an empty header row: the one thing the page wanted
 * you to do, rendered as the smallest thing on it. Worse, it was INSIDE the
 * grid, so it inherited the table's width and left the button floating
 * wherever the columns happened to end.
 *
 * It is the whole surface now, because at zero documents there is nothing else
 * on the page worth looking at. The dropzone is the app's shared
 * `FileUploader` - same extensions, same per-file cap, same security checks as
 * every other upload - and the columns are shown as chips so it is clear what
 * is about to be asked of the documents you drop.
 *
 * ONE BUTTON, NOT TWO. Uploading and running are a single act here:
 * `streamUploadAndProcess` sends the documents in batches and queues each one
 * for analysis as it lands. Splitting it into "upload" then "run" would invent
 * a state - documents present, analysis not started - that this screen has no
 * reason to leave anyone in.
 *
 * The populated grid is untouched: the moment a row exists, this is gone.
 */
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store';
import {
  PiFilesLight,
  PiPlayLight,
  PiColumnsLight,
  PiBookOpenTextLight,
} from 'react-icons/pi';
import FileUploader from '../shared/TailwindComponents/FileUploader';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import { streamUploadAndProcess } from '../../store/actions/BatchTables/BatchTablesActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { TableColumn } from '../../types/batchTables';

/** How many column names to name before counting the rest. */
const COLUMN_CHIP_LIMIT = 4;

interface Props {
  tableUuid: string;
  columns: TableColumn[];
  playbookName?: string | null;
  /** Gate from the licence check - the CTA explains itself when closed. */
  canRun?: boolean;
  runDisabledTooltip?: string;
}

const TableEmptyState: React.FC<Props> = ({
  tableUuid,
  columns,
  playbookName,
  canRun = true,
  runDisabledTooltip,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [files, setFiles] = useState<File[]>([]);
  const [starting, setStarting] = useState(false);

  const handleRun = useCallback(() => {
    if (!files.length || !tableUuid) return;
    setStarting(true);
    /*
      Fire-and-forget by design - `streamUploadAndProcess` keeps running after
      this component unmounts, which it does as soon as the first batch lands
      and the grid has rows. The toast is the handover; progress belongs to the
      header's uploading indicator from here on.
    */
    dispatch(streamUploadAndProcess(tableUuid, files) as any);
    dispatch(showFeedbackModal({
      showModal: true,
      modalType: 'success',
      message: t('Uploading {{count}} document(s)…', { count: files.length }),
      duration: 2500,
    }));
  }, [dispatch, files, tableUuid, t]);

  const shown = columns.slice(0, COLUMN_CHIP_LIMIT);
  const overflow = columns.length - shown.length;

  return (
    <div className="flex h-full min-h-0 items-center justify-center p-6">
      <div className="glass w-full max-w-xl rounded-lg p-6 shadow-sm">
        <div className="flex flex-col items-center text-center">
          <span
            aria-hidden
            className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-champagne/30 bg-champagne/10"
          >
            <PiFilesLight className="h-6 w-6 text-champagne" />
          </span>
          <h3 className="text-sm font-medium text-fg-strong">
            {t('Add documents to analyze')}
          </h3>
          <p className="mt-1 max-w-sm text-sm leading-relaxed text-fg-muted">
            {t('Drag them in or browse for them. Each document becomes a row, and every column below is answered against it.')}
          </p>
        </div>

        {/* THE dropzone - the shared uploader, so the accepted types, the size
            cap and the magic-byte checks are the app's and not this screen's. */}
        <div className="mt-5">
          <FileUploader files={files} onFilesChange={setFiles} />
        </div>

        {/*
          WHAT WILL RUN. The columns are the question the documents get asked,
          and at zero rows the grid that would show them is not on screen - so
          they are named here rather than left implicit.
        */}
        {(columns.length > 0 || playbookName) && (
          <div className="mt-5 border-t border-hairline-soft pt-4">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-fg-muted">
                <PiColumnsLight className="h-3.5 w-3.5" />
                {t('Will be answered for each document')}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {shown.map((column) => (
                <Badge key={column.id} color="gray" className="text-xs">{column.name}</Badge>
              ))}
              {overflow > 0 && (
                <span className="text-xs text-fg-muted">
                  {t('and')} {overflow} {t('more')}
                </span>
              )}
            </div>
            {playbookName && (
              <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-fg-muted">
                <PiBookOpenTextLight className="h-3.5 w-3.5 flex-shrink-0" />
                {t('Checked against')} <span className="text-fg-body">{playbookName}</span>
              </p>
            )}
          </div>
        )}

        <div className="mt-5 flex items-center justify-between gap-3 border-t border-hairline pt-4">
          {/* The count, so the button's effect is legible before pressing it. */}
          <span className="text-xs text-fg-muted">
            {files.length === 0
              ? t('No documents selected yet')
              : `${files.length} ${files.length === 1 ? t('document ready') : t('documents ready')}`}
          </span>
          <Button
            variant="primary"
            size="small"
            onClick={handleRun}
            disabled={!files.length || !canRun || starting}
            isLoading={starting}
            tooltip={!canRun ? runDisabledTooltip : undefined}
            icon={<PiPlayLight className="h-4 w-4" />}
            data-testid="table-empty-run"
          >
            {t('Upload & analyze')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TableEmptyState;
